import { WikiContent, WikiContentType } from 'src/components/wiki/wikidocument/wikicontent/WikiContent'
import { WikiDocument } from 'src/components/wiki/wikidocument/WikiDocument'
import { WikiSection } from 'src/components/wiki/wikidocument/wikisection/WikiSection'
import { WebServiceClient } from '../WebServiceClient'

/**
 * Defining the possible context tags
 */
export enum ContextTag {
  BLUE_SPOT_EXTREME_RAIN = 'blue_spot_extreme_rain',
  BOREHOLE = 'borehole',
  BURIED_VALLEYS = 'buried_valleys',
  ONSHORE_WINDMILLS = 'onshore_windmills',
  GERDA = 'gerda',
  GROUND_POLLUTION_1 = 'ground_pollution_1',
  GROUND_POLLUTION_2 = 'ground_pollution_2',
  HYDROMODEL = 'hydromodel',
  ICE_AGE = 'ice_age',
  MODEL_AREAS = 'model_area',
  MORPHOLOGY = 'morphology',
  ORTO = 'orto',
  TOPO_LOW = 'topo_low_background',
  TOPO_HIGH = 'topo_high_background',
  PRUSSIAN = 'prussian_background',
  OSM = 'osm',
  PESTICIDE_WATER_SUPPLY_WELLS = 'pesticide_water_supply_wells',
  PESTICIDE_WATERWORKS = 'pesticide_waterworks',
  POSTGLACIAL_SEDIMENT_THICKNESS = 'postglacial_sediment_thickness',
  PREQUATERNARY_LITHOLOGY = 'prequaternary_lithology',
  PREQUATERNARY_ELEVATION = 'prequaternary_elevation',
  ELEVATION_DEPTH = 'elevation_depth',
  PROTECTED_NATURE_TYPES = 'protected_nature_types',
  RAW_MATERIALS = 'raw_materials',
  SEABED_SEDIMENTS = 'seabed_sediments',
  SEA_LEVEL_ON_LAND = 'sea_level_on_land',
  SHALLOW_GROUNDWATER = 'shallow_groundwater',
  SLICE_DHM = 'slice_dhm',
  SLICE_KOTE = 'slice_kote',
  SOIL_TYPES = 'soil_types',
  TERRAIN_SHADOWS = 'terrain_shadows',
  VARV = 'varv',
  WATER_EXTRACTION_PLANTS = 'water_extraction_plants',
  WINDMILLS_AT_SEA = 'windmills_at_sea',
  WIND_SPEED = 'wind_speed',
  BIRD_PROTECTION = 'natura',
  RAMSAR = 'natura',
  HABITAT_PROTECTION = 'natura',
  FOREST_PROTECTION = 'natura',
  WILDLIFE = 'natura',
  NATURA_STATE_OWNED = 'natura',
  NATURA = 'natura',
  SCREEN_CLASSIC = 'screen_classic',
  SCREEN_SUBDUED = 'screen_subdued',
  SCREEN_GREY = 'screen_grey',
  COSTAL_LOCATIONS = 'costal_locations'
}

/**
 * Simple location interface for use in the wiki.
 */
export interface WikiLocation {
  key: string
  lon: number
  lat: number
  layer?: string
}


/**
 * Interface for representing the metadata of a WikiDocument as returned by the Django backend.
 */
export interface WikiDocumentMeta {
  key: string
  title: string
  tags: ContextTag[]
  categories: string[]
  location?: WikiLocation
}

/**
 * Web service clinet for fetching the wiki documents and data related to these.
 */
export class WikiService extends WebServiceClient {
  static get URL (): string {
    // For development it can be handy to have the Wiki host running elsewhere,
    // we therefore check to see if a separate host is mentioned in the environment.
    if (process.env.REACT_APP_WIKI_HOST !== undefined) {
      return `${process.env.REACT_APP_WIKI_HOST}/wiki`
    } else {
      // This is the standard deployment - the wiki is served by the same webserver
      // as the React site.
      return `${process.env.PUBLIC_URL}/wiki`
    }
  }

  /**
   * Constructor.
   */
  constructor () {
    super(WikiService.URL, {})
  }

  /**
   * Returns an URL for fetching a image based on the given parameters.
   * The image is scaled if either width or height is given.
   * Aspect ratio is maintained if just one of the dimentions is given.
   * @param imageId The id of the image to be fetched.
   * @param width The desired width of the image.
   * @param height The desired height of the image.
   * @returns An URL for fetching the image.
   */
  getImageUrl (imageId: string, width?: number, height?: number): string {
    this.setPath(`/image/${imageId}`)
    this.setParams({})
    if (width !== undefined) {
      if (width < 10 || width > 2048) {
        throw new Error(`Width must be a number in the range 10 - 2048. Got a width of "${width}".`)
      }
      this.addParam('width', `${width}`)
    }
    if (height !== undefined) {
      if (height < 10 || height > 2048) {
        throw new Error(`Height must be a number in the range 10 - 2048. Got a height of "${height}".`)
      }
      this.addParam('height', `${height}`)
    }

    return this.url()
  }

  /**
   * Fetches meta data for all the wiki documents on the backend.
   * @returns A promise bearing a list of wiki document meta objects.
   */
  async fetchDocumentsMeta (): Promise<WikiDocumentMeta[]> {
    this.setPath('/titles')
    this.setParams({})

    const response: Response = await fetch(this.url())
    if (!response.ok) {
      throw new Error('Got invalid HTTP response.')
    }

    return await response.json()
  }

  /**
   * Fethes the wiki document matching a given id.
   * @param documentId The id for which to fetch the matching wiki document.
   * @returns A promise bearing the wiki document.
   */
  async fetchDocument (documentId: string): Promise<WikiDocument> {
    this.setPath(`/doc/${documentId}`)
    this.setParams({})

    const response: Response = await fetch(this.url())
    if (!response.ok) {
      console.error(`No wiki document found at: ${this.url()}`)
      throw new Error('Got invalid HTTP response.')
    }

    return await response.json()
      .then(json => {
        return this.validateWikiDocument(json)
      })
  }

  /**
   * Validates (type checks) a wiki document.
   * @param json The JSON object retrieved from the wiki server.
   * @returns A WikiDocument structure based on the given JSON.
   */
  validateWikiDocument (json: any): WikiDocument {
    if (json.title === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. The title is missing.`)
    }
    if (typeof json.title !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. The title is of the wrong type. The expected type is string.`)
    }
    if (json.sections === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. It has no sections key.`)
    }
    if (!Array.isArray(json.sections)) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. Its sections key is not an array.`)
    }
    if (json.logo !== undefined && typeof json.logo !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. The logo is of the wrong type. The expected type is string.`)
    }
    if (json.resume !== undefined && typeof json.resume !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. The resume is of the wrong type. The expected type is string.`)
    }
    if (json.footer !== undefined && typeof json.footer !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. The footer is of the wrong type. The expected type is string.`)
    }
    if (json.categories === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. It has no categories key.`)
    }
    if (!Array.isArray(json.categories)) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. Its categories key is not an array.`)
    }

    // Sanitize sections.
    const sections: WikiSection[] = json.sections.map((s: any) => this.validateWikiSection(s))
    // Sanitize categories.
    const categories: string[] = json.categories.map((s: any) => {
      if (typeof s !== 'string') {
        throw new Error(`Invalid wiki document found at: ${this.url()}. A category is of wrong type. The expected type is string.`)
      }
      return s
    })

    return {
      ...json,
      sections: sections,
      categories: categories
    }
  }

  /**
   * Validates (type checks) a wiki section.
   * @param section The JSON object retrieved from the wiki server.
   * @returns A WikiSection structure based on the given JSON.
   */
  validateWikiSection (section: any): WikiSection {
    if (section.id === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A section is missing its id.`)
    }
    if (typeof section.id !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A section id is of wrong type. The expected type is string.`)
    }
    if (section.title === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A section is missing its title.`)
    }
    if (typeof section.title !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A section title is of wrong type. The expected type is string.`)
    }
    if (section.content === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A section is missing its content.`)
    }

    return {
      ...section,
      content: section.content.map((c: any) => this.validateWikiContent(c))
    }
  }

  /**
   * Validates (type checks) a wiki content object.
   * @param content The JSON object retrieved from the wiki server.
   * @returns A WikiContent structure based on the given JSON.
   */
  validateWikiContent (content: any): WikiContent {
    if (content.type === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A piece of content is missing its type.`)
    }
    if (!Object.keys(WikiContentType).includes(content.type)) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A piece of content has invalid type. The expected type is WikiContentType enum.`)
    }
    if (content.text === undefined) {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A piece of content is missing its text.`)
    }
    if (typeof content.text !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A piece of content has a text field of invalid type.  The expected type is string.`)
    }

    if (content.key !== undefined && typeof content.key !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A piece of content has a key field of invalid type.  The expected type is string.`)
    }
    if (content.url !== undefined && typeof content.url !== 'string') {
      throw new Error(`Invalid wiki document found at: ${this.url()}. A piece of content has a url field of invalid type.  The expected type is string.`)
    }

    return content
  }
}
